import { useEffect, useMemo, useState } from 'preact/hooks'
import { getManyProductsBySKU } from 'hooks/useStorefront'
import { getSkuQueryEdges } from 'helpers/storefront-graphql-helper'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import ProductVariant from 'snippets/product-variant/product-variant'
import { processSwatchData, orderingArray } from 'helpers/swatches'
import { Swatch } from './swatch'
import { SizeSwatch } from './size-swatch'

/**
 * @typedef PropsProductLinked
 * @property { string } className
 * @property { Array<string> } tags
 * @property { string } handle
 * @property { number } [limit]
 * @property { Function } setCurrentSwatch
 * @property { Array<string>} linkedSKUs
 */

/**
 * @typedef CD_SWATCHES
 * @property { string } handle
 * @property { string } colour_name
 * @property { string } colour_hex
 * @property { string } colour_prefix
 */

/**
 *
 * @param { PropsProductLinked} param0
 * @returns { React.FC }
 */
function ProductLinked({ className, handle, setCurrentSwatch, linkedSKUs, sku, currentSize }) {
  const [swatches, setSwatches] = useState([])
  const currentSwatch = useMemo(() => swatches.find((swatch) => swatch.handle === handle), [swatches])
  const currentVariant = useMemo(() => currentSwatch?.variants[0], [currentSwatch])
  const currentColor = useMemo(() => swatches.find((swatch) => swatch.handle === handle)?.color?.handle, [swatches])
  const [isLoading, setIsLoading] = useState(true)

  const updateCurrentVariant = (variant) => {
    const productHandle = variant.product.handle
    if (productHandle === handle) {
      return
    }
    window.location.href = `/products/${productHandle}`
  }

  useEffect(() => {
    // required to be able to set current state
    if (!linkedSKUs.includes(sku)) {
      linkedSKUs.unshift(sku)
    }
    const getSwatches = async () => {
      const relatedProducts = await getManyProductsBySKU(linkedSKUs)
        .then((res) => {
          if (!res.ok || !res.json) {
            throw res
          }
          return res.json()
        })
        .then((body) => {
          if (body.errors) {
            throw body
          }
          const skuEdges = getSkuQueryEdges(body)
          const products = processSwatchData(skuEdges).filter(product => product?.sku.some(sku => linkedSKUs.includes(sku)))
          const swatches = products.filter((swatch) => swatch.color?.hex || swatch.color?.image?.src)
          // for debugging
          const missingSwatches = products.filter((swatch) => !swatch.color?.hex && !swatch.color?.image?.src)
          if (missingSwatches.length) {
            console.warn('Some swatches missing in product-linked on this product', {
              handle,
              skuQueryResults: skuEdges,
              products,
              swatches,
              missingSwatches
            })
          }
          return swatches
        })
        .catch((err) => {
          console.error('Some error during getSwatches', err)
          return []
        })
      globalEmitter.emit(emitterProps.product.productLinked, relatedProducts)
      setSwatches(relatedProducts)
      setIsLoading(false)
    }
    getSwatches()
  }, [currentSize, handle])

  useEffect(() => {
    if (setCurrentSwatch) {
      setCurrentSwatch(currentSwatch)
    }
  }, [currentSwatch])

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="my-5 w-full">
          <div className="h-6 w-full bg-grey-200"></div>
          <div className="mt-2 h-10 w-full bg-grey-200"></div>
        </div>
        <div className="my-5 w-full">
          <div className="h-6 w-full bg-grey-200"></div>
          <div className="mt-2 h-10 w-full bg-grey-200"></div>
        </div>
      </div>
    )
  }

  if (!swatches.length || !currentSwatch) {
    return <></>
  }

  const uniqueColors = [...new Set(swatches.map((product) => product.color.handle).filter((color) => color))]
    //
    .sort()

  const uniqueSizes = [...new Set(swatches.map((product) => product.size).filter((size) => size))]
    // array is already unique, so do not need to worry about a === b
    .sort((a, b) => (orderingArray.indexOf(a) > orderingArray.indexOf(b) ? 1 : -1))

  return (
    <>
      {uniqueColors.length > 0 && (
        <div className={className}>
          <span className="product-linked-title mb-2 block text-sm font-medium text-grey-1000">
            Colour: <span className="font-normal capitalize text-grey-700">{currentSwatch?.prettyColorName ?? currentSwatch?.color.name}</span>
          </span>
          <div className="product-linked-items mb-4 flex gap-2.5 gap-y-3">
            {uniqueColors.map((color) => {
              const key = `${handle}_color.${color}_size.${currentSize}`
              const swatchProduct = swatches.find((product) => product.size === currentSize && product.color.handle === color)
              const cdSwatch = CD_STATE.CD_SWATCHES.find((cdSwatch) => cdSwatch.handle === color.toLowerCase())
              const hex = cdSwatch?.hex
              const image = cdSwatch?.image
              if (!swatchProduct) {
                return <Swatch key={key} isActive={false} title={color} image={image} hex={hex} isAvailable={false} />
              }
              if (swatchProduct.handle === handle) {
                return (
                  <Swatch
                    key={key}
                    isActive={color === currentColor}
                    image={image}
                    title={swatchProduct.prettyColorName ?? swatchProduct.color.name}
                    hex={hex}
                  />
                )
              }
              const url = `/products/${swatchProduct.handle}`
              return (
                <Swatch
                  key={key}
                  url={url}
                  isActive={color === currentColor}
                  image={image}
                  title={swatchProduct.prettyColorName ?? swatchProduct.color.name}
                  hex={hex}
                />
              )
            })}
          </div>
        </div>
      )}
      {uniqueSizes.length > 0 && (
        <div className={`${className} mb-4 md:mb-0`}>
          <span className="product-linked-title mb-2 block text-sm font-medium text-grey-1000">
            Size: <span className="font-normal capitalize text-grey-700">{currentSwatch?.size}</span>
          </span>
          {uniqueSizes.length > 1 && (
            <div className="product-linked-items flex flex-[0_0_50%] gap-2.5 gap-y-3">
              {uniqueSizes.map((size) => {
                const key = `${handle}_color.${currentColor}_size.${size}`
                const swatchProduct = swatches.find((product) => product.color.handle === currentColor && product.size === size)
                if (!swatchProduct) {
                  return <SizeSwatch key={key} isActive={false} title={size} text={size} isAvailable={false} />
                }
                if (swatchProduct.handle === handle) {
                  return <SizeSwatch key={key} isActive={size === currentSize} text={size} title={size} />
                }
                const url = `/products/${swatchProduct.handle}`
                return <SizeSwatch key={key} url={url} isActive={size === currentSize} text={size} title={size} />
              })}
            </div>
          )}
        </div>
      )}
      <ProductVariant
        variants={currentSwatch.variants}
        options={currentSwatch.options}
        updateCurrentVariant={updateCurrentVariant}
        currentVariant={currentVariant}
        updateUrl={false}
        displayType="button"
      />
    </>
  )
}

export default ProductLinked
